import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { brokers } from '../services/api.js';
import { useAuth } from '../auth/AuthContext.jsx';
import './BrokerDashboard.css';

const TOP_BROKERS_DATA = [
  { name: 'CRC Group', premium: 4200000 },
  { name: 'Amwins', premium: 3800000 },
  { name: 'RT Specialty', premium: 3100000 },
  { name: 'USI Insurance', premium: 2700000 },
  { name: 'Burns & Wilcox', premium: 2100000 },
];

const GEO_DATA = [
  { state: 'CA', count: 12 },
  { state: 'NY', count: 10 },
  { state: 'TX', count: 8 },
  { state: 'FL', count: 7 },
  { state: 'IL', count: 6 },
  { state: 'PA', count: 5 },
  { state: 'GA', count: 4 },
  { state: 'NJ', count: 4 },
  { state: 'OH', count: 3 },
  { state: 'MA', count: 3 },
];

function formatCurrency(val) {
  if (val == null) return '—';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val}`;
}

function formatChartCurrency(val) {
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val}`;
}

const EMPTY_FORM = {
  Name: '',
  Type: 'wholesale',
  ContactEmail: '',
  MasterCode: '',
  Phone: '',
  Address: '',
  City: '',
  State: '',
  ZipCode: '',
};

export function BrokerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [brokerList, setBrokerList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const loadBrokers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await brokers.list();
      const data = result.data || result;
      setBrokerList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load brokers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBrokers();
  }, [loadBrokers]);

  const filtered = brokerList.filter((b) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (b.Name || '').toLowerCase().includes(q) ||
      (b.ContactEmail || '').toLowerCase().includes(q) ||
      (b.MasterCode || '').toLowerCase().includes(q)
    );
  });

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      await brokers.create({ ...formData, Active: true });
      setShowAddForm(false);
      setFormData({ ...EMPTY_FORM });
      await loadBrokers();
    } catch (err) {
      setSaveError(err.message || 'Failed to create broker');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bd-loading">
        <div className="spinner" />
        <p>Loading brokers...</p>
      </div>
    );
  }

  if (error && brokerList.length === 0) {
    return (
      <div className="bd-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={loadBrokers}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bd">
      {/* Header */}
      <div className="bd__header">
        <h1 className="bd__title">Brokers</h1>
        <button
          className="btn btn--primary"
          onClick={() => setShowAddForm(true)}
        >
          + Add Broker
        </button>
      </div>

      {/* Add Broker Modal */}
      {showAddForm && (
        <div className="bd__modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="bd__modal card" onClick={(e) => e.stopPropagation()}>
            <div className="bd__modal-header">
              <h2 className="bd__modal-title">Add New Broker</h2>
              <button
                className="bd__modal-close"
                onClick={() => setShowAddForm(false)}
              >
                &times;
              </button>
            </div>
            <form className="bd__form" onSubmit={handleCreate}>
              <div className="bd__form-grid">
                <div className="bd__form-field">
                  <label className="bd__form-label">Name *</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    required
                    value={formData.Name}
                    onChange={(e) => handleFormChange('Name', e.target.value)}
                    placeholder="Broker name"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Type *</label>
                  <select
                    className="bd__form-input"
                    value={formData.Type}
                    onChange={(e) => handleFormChange('Type', e.target.value)}
                  >
                    <option value="wholesale">Wholesale</option>
                    <option value="retail">Retail</option>
                  </select>
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Contact Email</label>
                  <input
                    className="bd__form-input"
                    type="email"
                    value={formData.ContactEmail}
                    onChange={(e) =>
                      handleFormChange('ContactEmail', e.target.value)
                    }
                    placeholder="email@example.com"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Master Code</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    value={formData.MasterCode}
                    onChange={(e) =>
                      handleFormChange('MasterCode', e.target.value)
                    }
                    placeholder="e.g. BRK-001"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Phone</label>
                  <input
                    className="bd__form-input"
                    type="tel"
                    value={formData.Phone}
                    onChange={(e) => handleFormChange('Phone', e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Address</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    value={formData.Address}
                    onChange={(e) => handleFormChange('Address', e.target.value)}
                    placeholder="Street address"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">City</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    value={formData.City}
                    onChange={(e) => handleFormChange('City', e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">State</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    maxLength={2}
                    value={formData.State}
                    onChange={(e) =>
                      handleFormChange('State', e.target.value.toUpperCase())
                    }
                    placeholder="CA"
                  />
                </div>
                <div className="bd__form-field">
                  <label className="bd__form-label">Zip Code</label>
                  <input
                    className="bd__form-input"
                    type="text"
                    value={formData.ZipCode}
                    onChange={(e) => handleFormChange('ZipCode', e.target.value)}
                    placeholder="90210"
                  />
                </div>
              </div>
              {saveError && <p className="bd__form-error">{saveError}</p>}
              <div className="bd__form-actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => setShowAddForm(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Create Broker'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="bd__search-wrap">
        <input
          className="bd__search"
          type="text"
          placeholder="Search brokers by name, email, or master code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="bd__search-clear" onClick={() => setSearch('')}>
            &times;
          </button>
        )}
      </div>

      {/* Top Brokers Chart */}
      <div className="bd__section">
        <h2 className="bd__section-title">Top Brokers by Premium</h2>
        <div className="bd__chart-card card">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={TOP_BROKERS_DATA}
              margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--color-border)' }}
              />
              <YAxis
                tickFormatter={formatChartCurrency}
                tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--color-border)' }}
              />
              <Tooltip
                formatter={(value) => [formatCurrency(value), 'Premium']}
                contentStyle={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 13,
                }}
              />
              <Bar
                dataKey="premium"
                fill="var(--color-primary)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Geographic Distribution */}
      <div className="bd__section">
        <h2 className="bd__section-title">Geographic Distribution</h2>
        <div className="bd__geo-grid">
          {GEO_DATA.map((item) => (
            <div key={item.state} className="bd__geo-card card">
              <span className="bd__geo-state">{item.state}</span>
              <span className="bd__geo-count">{item.count}</span>
              <span className="bd__geo-label">offices</span>
            </div>
          ))}
        </div>
      </div>

      {/* Broker List Table */}
      <div className="bd__section">
        <h2 className="bd__section-title">
          All Brokers
          <span className="bd__count">{filtered.length}</span>
        </h2>
        <div className="bd__table-wrap card">
          <table className="bd__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Master Code</th>
                <th>Location</th>
                <th>Contact Email</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? (
                filtered.map((b) => (
                  <tr
                    key={b.BrokerID}
                    className="bd__table-row"
                    onClick={() => navigate(`/brokers/${b.BrokerID}`)}
                  >
                    <td className="bd__table-name">{b.Name || '—'}</td>
                    <td>
                      <span
                        className={`bd__type-badge bd__type-badge--${b.Type || 'wholesale'}`}
                      >
                        {b.Type || '—'}
                      </span>
                    </td>
                    <td className="bd__table-code">{b.MasterCode || '—'}</td>
                    <td>
                      {[b.City, b.State].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="bd__table-email">
                      {b.ContactEmail || '—'}
                    </td>
                    <td>
                      <span
                        className={`bd__status-badge ${b.Active !== false ? 'bd__status-badge--active' : 'bd__status-badge--inactive'}`}
                      >
                        {b.Active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="bd__table-empty">
                    {search
                      ? 'No brokers match your search.'
                      : 'No brokers found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
